import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang = 'en' } = useParams<{ lang: string }>();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setLangOpen(false);
      }
      if (mobileRef.current && !mobileRef.current.contains(event.target as Node)) {
        setMobileMenuOpen(false);
        setMobileCategoryOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll to top when navigating
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  const currentLang = i18n.language || 'en';

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Français' },
    { code: 'da', label: 'Dansk' },
    { code: 'de', label: 'Deutsch' },
  ];

  const handleLangChange = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
    setMobileMenuOpen(false);
    navigate(`/${code}/`);
  };

  const navLinks = [
    { label: t('nav_home'), path: `/${currentLang}/` },
    { label: 'Shopping Guide', path: `/${currentLang}/article/allchinabuy-spreadsheet-complete-guide` },
    { label: t('nav_guides'), path: `/${currentLang}/article/allchinabuy-beginner-guide` },
    { label: t('nav_reviews'), path: `/${currentLang}/article/allchinabuy-review` },
    { label: t('nav_shipping'), path: `/${currentLang}/article/allchinabuy-shipping-guide` },
    { label: t('nav_qc'), path: `/${currentLang}/article/allchinabuy-qc-guide` },
    { label: t('nav_safety'), path: `/${currentLang}/article/allchinabuy-safety-guide` },
  ];

  return (
    <nav className="w-full bg-white border-b border-gray-200/80 sticky top-0 z-50 shadow-sm">
      <div className="w-full px-4 sm:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <a href={`/${currentLang}/`} className="flex items-center gap-2 text-lg font-bold text-foreground-950 whitespace-nowrap flex-shrink-0">
            <span className="text-primary-700">AllChinaBuy</span>
            <span className="text-foreground-700">Spreadsheet</span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden xl:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.path + link.label}
                href={link.path}
                className="px-3 py-2 text-sm text-foreground-600 hover:text-primary-700 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Category Dropdown Desktop */}
            <div className="relative hidden md:block" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 px-4 py-2.5 text-sm bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors whitespace-nowrap font-medium"
              >
                <i className="ri-apps-line" />
                <span>{t('dropdown_label')}</span>
                <i className={`ri-arrow-down-s-line transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 max-h-[70vh] overflow-y-auto py-2 z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <span className="w-6 h-6 rounded bg-background-100 flex items-center justify-center text-xs text-foreground-500">
                        <i className="ri-shopping-bag-line" />
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Language Switcher Desktop */}
            <div className="relative hidden sm:block" ref={langRef}>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1 px-3 py-2.5 text-sm bg-secondary-50 text-secondary-700 rounded-lg hover:bg-secondary-100 transition-colors whitespace-nowrap font-medium"
              >
                <i className="ri-global-line" />
                <span className="uppercase">{currentLang}</span>
                <i className={`ri-arrow-down-s-line transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50">
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => handleLangChange(l.code)}
                      className={`block w-full text-left px-4 py-2.5 text-sm transition-colors ${
                        currentLang === l.code
                          ? 'bg-primary-50 text-primary-700 font-medium'
                          : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-900'
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Hamburger Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="xl:hidden flex items-center justify-center w-10 h-10 rounded-lg bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors"
            >
              <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-xl`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu - FIXED: added overflow-y-auto and max-h */}
      {mobileMenuOpen && (
        <div 
          ref={mobileRef} 
          className="xl:hidden border-t border-gray-200 bg-white overflow-y-auto max-h-[calc(100vh-4rem)]"
        >
          <div className="px-4 py-4 space-y-1">
            {/* Mobile Nav Links */}
            {navLinks.map((link) => (
              <a
                key={link.path + link.label}
                href={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors"
              >
                {link.label}
              </a>
            ))}

            {/* Mobile Category Accordion */}
            <div className="pt-2 border-t border-gray-100 mt-2">
              <button
                onClick={() => setMobileCategoryOpen(!mobileCategoryOpen)}
                className="flex items-center justify-between w-full px-4 py-3 text-sm font-medium text-foreground-900 hover:bg-background-100 rounded-lg transition-colors"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-apps-line text-primary-600" />
                  {t('dropdown_label')}
                </span>
                <i className={`ri-arrow-down-s-line transition-transform ${mobileCategoryOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileCategoryOpen && (
                <div className="mt-1 space-y-0.5 max-h-60 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setMobileCategoryOpen(false);
                      }}
                    >
                      <span className="w-5 h-5 rounded bg-background-100 flex items-center justify-center text-xs">
                        <i className="ri-shopping-bag-line" />
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Language Switcher */}
            <div className="pt-2 border-t border-gray-100 mt-2">
              <p className="px-4 py-2 text-xs font-medium text-foreground-500 uppercase tracking-wider">Language</p>
              <div className="grid grid-cols-2 gap-2 px-4">
                {languages.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => handleLangChange(l.code)}
                    className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                      currentLang === l.code
                        ? 'bg-primary-50 text-primary-700 font-medium border border-primary-200'
                        : 'text-foreground-700 hover:bg-background-100 border border-gray-200'
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}